import { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui';
import { VelocityDisplay } from '@/components/dashboard/VelocityCard';
import { velocityLabel } from '@/components/dashboard/velocityLabel';
import { useUserEvalHistory } from '@/hooks/useAthleteHistory';
import { formatDate } from '@/utils/formatters';
import type { UserEvalHistoryRow } from '@/services/evaluationApi';

// ─── Chart tooltip ────────────────────────────────────────────────────────────

// BD-7 grade-feel scoring (track C, story 8): recharts carries the full
// original data point through to `payload[i].payload`. When a point's
// `gradeActive` flag is set (see chartData below — only the Overall Score
// series ever sets it, since grade scale only affects overall_score), show a
// quiet annotation line. No letters, no recompute — just a note that a
// coach-configured grading scale produced this point's number. Absent flag
// (legacy evals, or the body-form/EOM series which never carry it) → this
// renders nothing new, matching today's tooltip exactly.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const gradeActive = payload[0]?.payload?.gradeActive === true;
  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '10px',
        padding: '10px 14px',
        boxShadow: 'var(--shadow-modal)',
        fontSize: '12px',
      }}
    >
      <p style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>{label}</p>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {payload.map((entry: any) => (
        <p key={entry.dataKey} style={{ color: entry.color, margin: '2px 0', fontWeight: 600 }}>
          {entry.name}: {Math.round(entry.value)}
        </p>
      ))}
      {gradeActive && (
        <p
          data-testid="grade-scale-tooltip-note"
          style={{
            color: 'var(--color-text-muted)',
            margin: '6px 0 0',
            fontSize: '11px',
            fontStyle: 'italic',
            fontWeight: 400,
          }}
        >
          Coach grading scale applied
        </p>
      )}
    </div>
  );
}

// ─── Mini trend chart ─────────────────────────────────────────────────────────

interface MiniChartProps {
  /**
   * gradeActive is optional and only ever populated on the Overall Score
   * series (grade scale affects overall_score only — CLAUDE.md rule #1).
   */
  data: { date: string; value: number | null; gradeActive?: boolean }[];
  color: string;
  title: string;
}

function MiniChart({ data, color, title }: MiniChartProps) {
  const filtered = data.filter((d) => d.value !== null) as {
    date: string;
    value: number;
    gradeActive?: boolean;
  }[];

  return (
    <Card>
      <CardHeader style={{ paddingBottom: '4px' }}>
        <CardTitle style={{ fontSize: '13px' }}>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {filtered.length < 2 ? (
          <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', padding: '12px 0' }}>
            Not enough data yet.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={filtered} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border-subtle)"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                name={title}
                stroke={color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: color, stroke: 'var(--color-dark-card)', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Score cell ───────────────────────────────────────────────────────────────

function ScoreCell({ value }: { value: number | null }) {
  if (value === null) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }
  const score = Math.round(value);
  const color =
    score >= 80
      ? 'var(--color-accent)'
      : score >= 60
      ? 'var(--color-accent-gold)'
      : 'var(--color-accent-red)';
  return (
    <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '14px', color }}>
      {score}
    </span>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AthleteHistoryPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useUserEvalHistory(userId, { limit: 50 });

  const rows: UserEvalHistoryRow[] = useMemo(() => data?.rows ?? [], [data]);
  const status = data?.status ?? 'loading';

  // Derive athlete display name from rows.
  // Phase A added display_name and email to UserEvalHistoryRow; all rows for the
  // same athlete share the same subject identity, so picking from rows[0] is safe.
  const athleteName = useMemo(() => {
    const first = rows[0];
    return (
      first?.display_name ??
      first?.email ??
      `Athlete ${userId?.slice(0, 8) ?? 'Unknown'}`
    );
  }, [rows, userId]);

  // Sort rows chronologically for charts
  const sorted = useMemo(
    () =>
      [...rows].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
    [rows]
  );

  // Velocity — computed from sorted rows via shared VelocityCard helper
  // UserEvalHistoryRow has the same overall_score field as EvaluationSummaryItem,
  // so we cast to the minimal shape velocityLabel needs.
  const velocity = useMemo(() => {
    if (sorted.length < 2) return null;
    return velocityLabel(sorted as unknown as import('@/services/evaluationApi').EvaluationSummaryItem[]);
  }, [sorted]);

  // Chart data
  // BD-7 grade-feel scoring (track C, story 8): gradeActive is derived from
  // grade_scale_applied.active when the row carries it, and tolerates
  // absence (legacy evals, or before a follow-up backend change adds the
  // field to this endpoint — see the field's doc comment in evaluationApi.ts).
  const chartData = useMemo(
    () =>
      sorted.map((r) => ({
        date: formatDate(r.created_at, 'MMM d'),
        overall: r.overall_score,
        bodyForm: r.body_form_score,
        eom: r.eom_score,
        gradeActive: r.grade_scale_applied?.active === true,
      })),
    [sorted]
  );

  // Reverse for table (most recent first)
  const tableRows = useMemo(() => [...rows].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  ), [rows]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Spinner size="xl" label="Loading athlete history..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 24px' }}>
        <p style={{ color: 'var(--color-danger)', marginBottom: '16px' }}>
          Failed to load athlete history.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            color: 'var(--color-accent-blue)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Go back
        </button>
      </div>
    );
  }

  if (status === 'no-access') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '60vh',
          gap: '12px',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <p
          style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            fontSize: '18px',
            color: 'var(--color-text-primary)',
          }}
        >
          Access restricted
        </p>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', maxWidth: '360px' }}>
          You don't have access to this athlete's history. They must be a member of one of your
          channels.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: '8px',
            color: 'var(--color-accent-blue)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes ahp-fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ahp-page { animation: ahp-fade-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>

      <div className="ahp-page" style={{ maxWidth: '960px', margin: '0 auto', paddingBottom: '48px' }}>

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-text-muted)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            marginBottom: '20px',
            padding: 0,
          }}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back
        </button>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <p
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              marginBottom: '4px',
            }}
          >
            Athlete
          </p>
          <h1
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '26px',
              color: 'var(--color-text-primary)',
              margin: 0,
              letterSpacing: '-0.3px',
            }}
          >
            {athleteName}
          </h1>
          <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
            {rows.length} evaluation{rows.length !== 1 ? 's' : ''} recorded
          </p>
          {/* Velocity indicator — uses shared VelocityDisplay (Feature 5) */}
          {velocity && (
            <div style={{ marginTop: '8px' }}>
              <VelocityDisplay velocity={velocity} />
            </div>
          )}
        </div>

        {/* Trend charts */}
        {sorted.length >= 2 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '16px',
              marginBottom: '28px',
            }}
          >
            <MiniChart
              title="Overall Score"
              color="#1ED760"
              data={chartData.map((d) => ({ date: d.date, value: d.overall, gradeActive: d.gradeActive }))}
            />
            <MiniChart
              title="Body Form Score"
              color="rgba(61, 169, 252, 0.9)"
              data={chartData.map((d) => ({ date: d.date, value: d.bodyForm }))}
            />
            <MiniChart
              title="Economy of Motion"
              color="rgba(244, 201, 93, 0.9)"
              data={chartData.map((d) => ({ date: d.date, value: d.eom }))}
            />
          </div>
        )}

        {/* Evaluations table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Evaluation History</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {tableRows.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '16px 0' }}>
                No evaluations found.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                      {['Date', 'Template', 'Triggered By', 'Overall', 'Body Form', 'EOM'].map(
                        (label) => (
                          <th
                            key={label}
                            style={{
                              textAlign: 'left',
                              padding: '8px 12px 8px 0',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.6px',
                              color: 'var(--color-text-muted)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => (
                      <tr
                        key={row.evaluation_id}
                        style={{
                          borderBottom: '1px solid var(--color-border-subtle)',
                          cursor: 'pointer',
                          transition: 'background-color 120ms ease',
                        }}
                        onClick={() => navigate(`/evaluations/${row.evaluation_id}`)}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                            'rgba(61, 169, 252, 0.04)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                            'transparent';
                        }}
                      >
                        <td style={{ padding: '12px 12px 12px 0', whiteSpace: 'nowrap' }}>
                          <span style={{ color: 'var(--color-text-secondary)' }}>
                            {formatDate(row.created_at)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 12px 12px 0' }}>
                          <Link
                            to={`/templates/${row.template_id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              color: 'var(--color-accent-blue)',
                              textDecoration: 'none',
                              fontSize: '13px',
                            }}
                          >
                            {row.template_name}
                          </Link>
                        </td>
                        <td style={{ padding: '12px 12px 12px 0' }}>
                          <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                            {row.created_by_display_name ?? 'self'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 12px 12px 0' }}>
                          <ScoreCell value={row.overall_score} />
                        </td>
                        <td style={{ padding: '12px 12px 12px 0' }}>
                          <ScoreCell value={row.body_form_score} />
                        </td>
                        <td style={{ padding: '12px 0' }}>
                          <ScoreCell value={row.eom_score} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
